import useResolvedFindingStatus from "hooks/useResolvedFindingStatus";
import useUnResolvedFindingStatus from "hooks/useUnResolvedFindingStatus";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { ParsedUrlDetails } from "types/pagination";
import { formatNumber } from "utils/numberUtil";
import { updateURLSearch } from "utils/urlUtil";

import ACPopover from "ac-components/ac-popover/ACPopover";

import "./ACResolvedUnresolvedFindingsBar.less";

type Props = {
  findingStatusCountMap: Record<string, number>;
  payload?: Partial<ParsedUrlDetails>;
};

export default function ACResolvedUnresolvedFindingsBar({
  findingStatusCountMap,
  payload = {},
}: Props) {
  const history = useHistory();
  const unResolvedStatus = useUnResolvedFindingStatus();
  const resolvedStatus = useResolvedFindingStatus();

  const resolvedCount = useMemo(() => {
    return resolvedStatus.reduce((count, status) => {
      return count + (findingStatusCountMap?.[status] || 0);
    }, 0);
  }, [findingStatusCountMap, resolvedStatus]);

  const unResolvedCount = useMemo(() => {
    return unResolvedStatus.reduce((count, status) => {
      return count + (findingStatusCountMap?.[status] || 0);
    }, 0);
  }, [findingStatusCountMap, unResolvedStatus]);

  const redirectToFindings = (type: string) => {
    const { filters, groupBy, savedSearch, pagination = { page: 0 } } = payload;
    const _filters = {
      ...filters,
      status: type === "resolved" ? resolvedStatus : unResolvedStatus,
    };
    updateURLSearch(
      history,
      {
        filters: _filters,
        pagination: pagination,
        savedSearch: savedSearch,
        groupBy: groupBy,
      },
      "/findings",
    );
  };

  const total = unResolvedCount + resolvedCount;
  const unResolvedPercentage = Math.round((unResolvedCount / total) * 100) || 0;
  const resolvedPercentage = Math.round((resolvedCount / total) * 100) || 0;

  return (
    <ACPopover
      style={{ padding: "unset" }}
      content={
        <table className="ac-findings-count-table">
          <tr
            className="clickable"
            onClick={() => redirectToFindings("unresolved")}
          >
            <td className="d-flex align-items-center">
              <div className="ac-findings-count-table-unresolved-bar" />
              <span>Unresolved</span>
            </td>
            <td>{formatNumber(unResolvedCount, "long")}</td>
          </tr>
          <tr
            className="clickable"
            onClick={() => redirectToFindings("resolved")}
          >
            <td className="d-flex align-items-center">
              <div className="ac-findings-count-table-resolved-bar" />
              <span>Resolved</span>
            </td>
            <td>{formatNumber(resolvedCount, "long")}</td>
          </tr>
          <tr className="ac-findings-count-table-total-row">
            <td>Total</td>
            <td>{formatNumber(total, "long")}</td>
          </tr>
        </table>
      }
      placement="top"
      trigger="hover"
    >
      <div className="ac-findings-count-bar">
        {total > 0 ? (
          <div className="ac-findings-bar-container">
            {/* Hiding %Values as per the requirement - ENG-60583 */}
            {/* {(unResolvedPercentage !== 0 || resolvedPercentage !== 0) && (
            <div className="ac-findings-bar-numbers">
              <div className="ac-findings-count-unresolved">
                {unResolvedPercentage}%
              </div>
              <div className="ac-findings-count-resolved">
                {resolvedPercentage}%
              </div>
            </div>
          )} */}
            {(unResolvedPercentage !== 0 || resolvedPercentage !== 0) && (
              <div className="ac-findings-bar">
                {resolvedPercentage > 0 ? (
                  <div
                    style={{
                      width:
                        unResolvedCount === 0
                          ? 128
                          : `calc(${resolvedPercentage}%)`,
                      minWidth: resolvedCount === 0 ? 0 : 4,
                    }}
                    className="ac-findings-bar-resolved"
                  />
                ) : null}
                {unResolvedPercentage > 0 ? (
                  <div
                    style={{
                      width:
                        resolvedCount === 0
                          ? 128
                          : `calc(${unResolvedPercentage}%)`,
                      minWidth: unResolvedCount === 0 ? 0 : 4,
                    }}
                    className="ac-findings-bar-unresolved"
                  />
                ) : null}
              </div>
            )}
          </div>
        ) : null}

        <div
          className="ac-findings-count-total"
          style={{ justifyContent: total === 0 ? "flex-start" : "flex-end" }}
        >
          {resolvedPercentage}%
        </div>
      </div>
    </ACPopover>
  );
}
